import classNames from 'classnames';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { BoxFileInterface, BoxShareLinkInterface } from 'src/common/interfaces/integrations/box.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { FullscreenModal } from 'src/editor/content-creation/components/fullscreen-modal';
import { BoxIntegrationIcon, CloseSmallIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';
import { useBoxIntegration } from 'src/utils/use-box-integration.hook';

interface Props {
  openRef: MutableRefObject<(() => void) | undefined>;
  onBoxFilePick: (boxLink: BoxShareLinkInterface) => void;
  isEditor?: boolean;
}

const LibraryBoxStatus = {
  Idle: 0,
  Loading: 1,
  Success: 2,
  Fail: 3,
};

type LibraryStatusType = typeof LibraryBoxStatus[keyof typeof LibraryBoxStatus];

interface BoxListItem {
  [id: string]: {
    hasLink: boolean;
    sharedLink: string;
    name: string;
  };
}

export const DashboardLibraryContentPickBoxFileModal = ({ openRef, onBoxFilePick, isEditor = false }: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<LibraryStatusType>(LibraryBoxStatus.Idle);
  const [boxItems, setBoxItems] = useState<BoxListItem>({});
  const { setErrorNotification } = useNotificationMessage();

  const modalCloseRef = useRef(functionNoop);

  const boxIntegration = useBoxIntegration();

  const onBoxFilePicked = async (id: string) => {
    const boxLink = boxItems[id];
    if (boxLink.hasLink) {
      onBoxFilePick({
        id,
        url: boxLink.sharedLink,
        name: boxLink.name,
      });
      return;
    }
    setIsProcessing(true);
    try {
      const fileShareLink = await boxIntegration.getShareLink(id);
      onBoxFilePick({
        id,
        url: fileShareLink.url,
        name: boxLink.name,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const initBox = async () => {
    try {
      const files = await boxIntegration.fetchAllFiles();
      const shareLinks = await boxIntegration.getFileShareLinks(files);

      const boxList = files.reduce(
        (acc, file) => {
          const shareLink = shareLinks.find((link) => link.id === file.id);

          acc[file.id] = {
            hasLink: !!shareLink,
            sharedLink: shareLink?.url || '',
            name: file.name,
          };

          return acc;
        },
        { ...boxItems }
      );

      setBoxItems(boxList);
      setStatus(LibraryBoxStatus.Success);
    } catch (err) {
      modalCloseRef.current();
      setErrorNotification('Failed to fetch Box files');
    }
  };

  const renderModalContent = () => {
    if (status === LibraryBoxStatus.Loading) {
      return <LoadingSpinner />;
    }
    const boxEntries = Object.entries(boxItems);

    return (
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-8'>
          <BoxIntegrationIcon className='w-12 h-6' />
          <div className='flex flex-col gap-4'>
            {boxEntries.map(([id, { name }]) => (
              <div key={id} className='flex gap-2 items-center justify-between'>
                <span className='truncate'>{name}</span>
                <div className='flex items-center gap-2 ml-auto'>
                  <button
                    type='button'
                    className='btn-bedrock-secondary px-4 py-1 space-x-1'
                    onClick={() => onBoxFilePicked(id)}
                  >
                    <span>Use</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className='absolute top-0 right-0'
          onClick={() => {
            modalCloseRef.current();
          }}
        >
          <CloseSmallIcon className='w-5 h-5 text-bedrock-p' />
        </div>
        <div className='flex'>
          <button
            type='button'
            disabled={isProcessing}
            className='btn-bedrock-primary px-4 py-1 ml-auto'
            onClick={() => {
              modalCloseRef.current();
            }}
          >
            <span>Ok</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <FullscreenModal
      wrapperClassName='min-w-96 min-h-40 m-auto'
      layoutStyle='desktop'
      openRef={openRef}
      onOpen={initBox}
      onClose={() => {
        setStatus(LibraryBoxStatus.Idle);
      }}
      closeRef={modalCloseRef}
      overlayClassName='bg-neue-glass-light neue-glass-backdrop-blur-light'
    >
      <div
        className={classNames('relative rounded-lg m-auto p-4 w-[40rem]', {
          'bg-neue-journey-bg-90': isEditor,
          'bg-white': !isEditor,
        })}
      >
        {renderModalContent()}
      </div>
    </FullscreenModal>
  );
};

// const files = [
//   {
//     type: 'folder',
//     id: '279408970958',
//     sequence_id: '0',
//     etag: '0',
//     name: 'blabla',
//   },
//   {
//     type: 'file',
//     id: '1623199506033',
//     file_version: {
//       type: 'file_version',
//       id: '1784664585343',
//       sha1: '51baa3735d348608e816f6e3751140470ab1abe7',
//     },
//     sequence_id: '2',
//     etag: '2',
//     sha1: '51baa3735d348608e816f6e3751140470ab1abe7',
//     name: 'blablabla.gdoc',
//   },
//   {
//     type: 'file',
//     id: '1615754888549',
//     file_version: {
//       type: 'file_version',
//       id: '1776432464549',
//       sha1: '1f59e84376b2acda296b1b431a16e5cd5dfb7da8',
//     },
//     sequence_id: '1',
//     etag: '1',
//     sha1: '1f59e84376b2acda296b1b431a16e5cd5dfb7da8',
//     name: 'sample-one-page.pdf',
//   },
//   {
//     type: 'file',
//     id: '1615744626724',
//     file_version: {
//       type: 'file_version',
//       id: '1776422449924',
//       sha1: '8f481abdbea65dc187463747496b16ced4256a3e',
//     },
//     sequence_id: '1',
//     etag: '1',
//     sha1: '8f481abdbea65dc187463747496b16ced4256a3e',
//     name: 'sample-three-page.pdf',
//   },
//   {
//     type: 'file',
//     id: '1615761297138',
//     file_version: {
//       type: 'file_version',
//       id: '1776438966738',
//       sha1: '3b0a5f34bf61d59e758e9391403cc412dece7ffa',
//     },
//     sequence_id: '1',
//     etag: '1',
//     sha1: '3b0a5f34bf61d59e758e9391403cc412dece7ffa',
//     name: 'sample-two-page.pdf',
//   },
//   {
//     type: 'file',
//     id: '1623245137312',
//     file_version: {
//       type: 'file_version',
//       id: '1784714595712',
//       sha1: '0121d98093b2242dd16d9808684c4525b6eb5129',
//     },
//     sequence_id: '1',
//     etag: '1',
//     sha1: '0121d98093b2242dd16d9808684c4525b6eb5129',
//     name: 'tattoo_video.mp4',
//   },
// ] as BoxFileInterface[];

// const shareLinks = [
//   { id: '1623199506033', name: 'blablabla.gdoc', url: 'https://app.box.com/s/xddofp7tpdnxag72taxgxlf8hff5a47o' },
//   {
//     id: '1615744626724',
//     name: 'sample-three-page.pdf',
//     url: 'https://app.box.com/s/010ps65cizx8uo406ualyqwiwzbgn6hr',
//   },
// ];
